<script setup>
import { defineProps, ref } from 'vue'
import { useRestoGLobalStore } from '@/stores/restoGlobalStore'
import { useCartGLobalStore } from '@/stores/cartGlobalStore'
import { addComplexItemFn } from '@/services/api/restApiCalls'
import { Dialog, DialogPanel, TransitionChild, TransitionRoot } from '@headlessui/vue'
import { XMarkIcon } from '@heroicons/vue/24/outline'
import { CheckIcon, MinusCircleIcon, PlusCircleIcon, CheckCircleIcon } from '@heroicons/vue/24/outline'
import { notify } from 'notiwind'

const restoStore = useRestoGLobalStore()
const cartStore = useCartGLobalStore()
const props = defineProps(['complexItemData', 'productInfo', 'itemCount'])
const showSuccessEffect = ref(false)
const acccessoryExtraParts = ref([])
const selectedAccesoryExtras = ref([])
const selectedOptionalExtra = ref({})
let currentAccesoryExtra = ref([])
let currentOptionalExtra = ref([])
let itemQuantity = ref(1)
const showComplexModal = ref(false)
const currentAccesoryMinCount = ref(1) // Dynamic count from current accessory group
const currentGroupSelections = ref(0) // Track selections for current accessory group
const skipOptionalExtras = ref(false) // Flag to skip showing optional extras

const handleComplexModalOpen = async (complexItem) => {
  if (cartStore.CART_DATA?.order_type == 'delivery' && !cartStore?.CART_DATA?.pc_data?.is_valid) {
    cartStore.SHOW_POSTCODE_MODAL = true
    return
  }
  if (complexItem.accesory_extra_parts) {
    acccessoryExtraParts.value = Object.values(complexItem?.accesory_extra_parts)
    currentAccesoryExtra.value = acccessoryExtraParts.value.shift() || []
    // Set the minimum count from the first accessory extra item
    if (currentAccesoryExtra.value.length > 0) {
      currentAccesoryMinCount.value = currentAccesoryExtra.value[0]?.choose_min_count || 1
    }
  }
  if (complexItem.optional_extras) {
    currentOptionalExtra.value = complexItem?.optional_extras
  }
  // Reset group selection counter when opening modal
  currentGroupSelections.value = 0
  showComplexModal.value = true
}

const handleSelectedAccessoryExtra = (extraItem) => {
  if (extraItem.skip_optional_extras == 1) {
    skipOptionalExtras.value = true
  }
  selectedAccesoryExtras.value = [...selectedAccesoryExtras.value, extraItem]
  currentGroupSelections.value += 1
  
  // Check if we've reached the required selection count for current accessory group
  if (currentGroupSelections.value >= currentAccesoryMinCount.value) {
    // Move to next accessory group if selection count is met
    let remaining_extra_parts = acccessoryExtraParts.value
    let current_extra_part = remaining_extra_parts.shift() || []
    currentAccesoryExtra.value = current_extra_part
    acccessoryExtraParts.value = remaining_extra_parts
    
    // Set the minimum count for the new accessory group
    if (current_extra_part.length > 0) {
      currentAccesoryMinCount.value = current_extra_part[0]?.choose_min_count || 1
    }
    
    // Reset selection count for next group
    currentGroupSelections.value = 0
  }
}
const handleAddSelectedOptionalExtra = (optionalExtra) => {
  let extra_id = optionalExtra['extra_id']
  let selectedOptionalExtraObjTMP = selectedOptionalExtra.value
  if (selectedOptionalExtraObjTMP[extra_id]) {
    selectedOptionalExtraObjTMP[extra_id]['quantity'] += 1
  } else {
    optionalExtra['quantity'] = 1
    selectedOptionalExtraObjTMP[extra_id] = optionalExtra
  }
  selectedOptionalExtra.value = { ...selectedOptionalExtra.value, ...selectedOptionalExtraObjTMP }
}
const handleRemoveSelectedOptionExtra = (optionalExtra) => {
  let extra_id = optionalExtra['extra_id']
  let selectedOptionalExtraObjTMP = selectedOptionalExtra.value
  if (selectedOptionalExtraObjTMP[extra_id]) {
    selectedOptionalExtraObjTMP[extra_id]['quantity'] -= 1
    if (selectedOptionalExtraObjTMP[extra_id]['quantity'] === 0) {
      delete selectedOptionalExtraObjTMP[extra_id]
    }
  }
  selectedOptionalExtra.value = { ...selectedOptionalExtra.value, ...selectedOptionalExtraObjTMP }
}
const handleAddComplexItem = async (complexItem) => {
  const complexItemData = {
    action: 'add_complex_item',
    resto_id: restoStore?.RESTO_ID,
    item_id: complexItem?.item_id,
    optional_extras: Object.values(selectedOptionalExtra.value),
    accesory_extras: selectedAccesoryExtras.value,
    quantity: itemQuantity.value
  }
  let complexItemFormData = new FormData()
  complexItemFormData.append('cart_data', JSON.stringify(complexItemData))
  const cart_info = await addComplexItemFn(complexItemFormData)
  cartStore.CART_DATA = cart_info?.[restoStore?.RESTO_ID] ?? {}
  cartStore.CAN_DO_CHECKOUT = cart_info?.can_do_checkout
  restoStore.hideModal()
  handleComplexModalClose()
  showSuccessEffect.value = true
  setTimeout(() => {
    showSuccessEffect.value = false
  }, 1200)
  notify(
    {
      group: 'success',
      title: 'Added',
      text: `${complexItem?.item_name} added to cart`
    },
    2000
  )
}
const handleComplexModalClose = () => {
  currentAccesoryExtra.value = []
  acccessoryExtraParts.value = []
  selectedAccesoryExtras.value = []
  currentOptionalExtra.value = []
  selectedOptionalExtra.value = {}
  currentGroupSelections.value = 0
  currentAccesoryMinCount.value = 1
  showComplexModal.value = false
  skipOptionalExtras.value = false
}

const handleItemQuantity = () => {
  if (itemQuantity.value > 1) {
    itemQuantity.value -= 1
  }
}
</script>

<template>
  <div
    v-if="complexItemData?.menu_img_url"
    class="flex items-center rounded-lg border border-gray-300 dark:border-gray-700 p-3 cursor-pointer hover:border-indigo-400 hover:shadow-md dark:hover:border-indigo-500 bg-white dark:bg-surface-dark transition-all duration-200 transform hover:scale-[1.01]"
    @click="complexItemData?.is_available == 0 ? null : handleComplexModalOpen(complexItemData)"
  >
    <div class="min-w-0 flex-1">
      <h3 as="h3" class="text-md font-semibold leading-6 text-gray-900 dark:text-text-dark">
        {{ complexItemData?.item_name_full }}
      </h3>
      <div v-if="itemCount == 1 && productInfo">
        <p class="line-clamp-2 text-sm text-gray-500 dark:text-gray-400 mt-1 mb-1">{{ productInfo }}</p>
      </div>
      <div class="mt-1 text-base font-medium text-gray-900 dark:text-text-dark">
        <span class="text-indigo-600 dark:text-indigo-400"> £{{ complexItemData?.price }} </span>
      </div>
    </div>
    <div v-if="complexItemData?.menu_img_url" class="flex-shrink-0 ml-3">
      <div
        class="h-16 w-16 rounded-md overflow-hidden shadow-sm"
        :style="{
          backgroundImage: 'url(' + complexItemData?.menu_img_url + ')',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }"
      ></div>
    </div>
    <div v-if="complexItemData?.is_available == 0" class="absolute -top-2.5 left-0">
      <span class="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-800">Unavailable</span>
    </div>
    <div
      v-if="showSuccessEffect"
      class="absolute inset-0 bg-green-100/95 rounded-xl flex items-center justify-center z-20 backdrop-blur-sm"
    >
      <div class="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-full shadow-lg animate-pulse">
        <CheckCircleIcon class="h-5 w-5 text-white" />
        <span class="font-medium text-sm">Added</span>
      </div>
    </div>
  </div>
  <div
    v-else
    class="rounded-lg border border-gray-300 dark:border-gray-700 p-3 cursor-pointer hover:border-indigo-400 hover:shadow-md dark:hover:border-indigo-500 bg-white dark:bg-surface-dark transition-all duration-200 transform hover:scale-[1.01]"
    @click="complexItemData?.is_available == 0 ? null : handleComplexModalOpen(complexItemData)"
  >
    <div class="flex justify-between items-center">
      <h3 as="h3" class="text-md font-semibold leading-6 text-gray-900 dark:text-text-dark">
        {{ complexItemData?.item_name_full }}
      </h3>
      <div class="ml-3 text-indigo-600 dark:text-indigo-400 font-medium">
        <span> £{{ complexItemData?.price }} </span>
      </div>
    </div>
    <div v-if="itemCount == 1 && productInfo">
      <p class="line-clamp-2 text-sm text-gray-500 dark:text-gray-400 mt-1 mb-0">{{ productInfo }}</p>
    </div>
    <div v-if="complexItemData?.is_available == 0" class="absolute -top-2.5 left-0">
      <span class="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-800">Unavailable</span>
    </div>
    <div
      v-if="showSuccessEffect"
      class="absolute inset-0 bg-green-100/95 rounded-xl flex items-center justify-center z-20 backdrop-blur-sm"
    >
      <div class="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-full shadow-lg animate-pulse">
        <CheckCircleIcon class="h-5 w-5 text-white" />
        <span class="font-medium text-sm">Added</span>
      </div>
    </div>
  </div>

  <TransitionRoot as="template" :show="showComplexModal">
    <Dialog as="div" class="relative z-20" @close="handleComplexModalClose()">
      <TransitionChild
        as="div"
        enter="ease-out duration-300"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-200"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 backdrop-blur-sm transition-opacity"></div>
      </TransitionChild>

      <div class="fixed inset-0 z-20 overflow-y-auto">
        <div class="flex min-h-full justify-center p-4 text-center items-center sm:p-0">
          <TransitionChild
            as="div"
            enter="ease-out duration-300"
            enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enter-to="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leave-from="opacity-100 translate-y-0 sm:scale-100"
            leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            class="w-full max-w-md"
          >
            <DialogPanel
              class="relative transform rounded-lg bg-white dark:bg-surface-dark px-4 pt-5 pb-4 text-left shadow-xl transition-all my-8 w-full max-w-md overflow-hidden"
            >
              <div class="absolute top-0 right-0 pt-2 pr-2 z-10 block">
                <button
                  type="button"
                  class="rounded-full border border-e-violet p-1 bg-white dark:bg-gray-700 text-e-violet hover:text-white hover:bg-e-violet dark:hover:bg-indigo-600 focus:outline-none shadow-md transition-colors duration-200"
                  @click="handleComplexModalClose()"
                >
                  <span class="sr-only">Close</span>
                  <XMarkIcon class="h-5 w-5" aria-hidden="true" />
                </button>
              </div>
              <div class="max-h-90vh overflow-auto mb-8">
                <h2 as="h2" class="text-xl font-bold leading-6 text-gray-900 dark:text-text-dark mb-4">
                  {{ complexItemData?.item_name }}
                </h2>
                <div
                  v-for="selectedAccExtra in selectedAccesoryExtras"
                  :key="selectedAccExtra.extra_id"
                  class="mt-3 mb-3"
                >
                  <div class="bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 p-3 rounded-md shadow-sm">
                    <div class="flex justify-between items-center">
                      <h3 as="h3" class="text-md font-semibold leading-6 text-gray-900 dark:text-text-dark">
                        {{ selectedAccExtra.extra_name }}
                      </h3>
                      <div class="flex ml-3">
                        <h3
                          v-if="selectedAccExtra.price > 0"
                          class="text-md font-medium leading-6 text-indigo-600 dark:text-indigo-400 mr-4"
                        >
                          £{{ selectedAccExtra.price }}
                        </h3>
                        <CheckIcon class="h-5 w-5 text-green-600 dark:text-green-400" aria-hidden="true" />
                      </div>
                    </div>
                    <div v-if="selectedAccExtra.info" class="mt-1">
                      <p class="text-sm text-gray-500 dark:text-gray-400">{{ selectedAccExtra.info }}</p>
                    </div>
                  </div>
                </div>
                <div v-if="currentAccesoryExtra.length" class="text-lg font-semibold text-indigo-600 dark:text-indigo-400 mb-2">
                  <span v-if="currentGroupSelections === 0">Choose any {{ currentAccesoryMinCount }}</span>
                  <span v-else>{{ currentGroupSelections }}/{{ currentAccesoryMinCount }} selected</span>
                </div>
                <div
                  v-for="accExtra in currentAccesoryExtra"
                  :key="accExtra.extra_id"
                  class="mt-3 mb-3"
                >
                  <div
                    class="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 p-3 cursor-pointer hover:bg-gray-50 hover:border-indigo-400 dark:hover:bg-gray-600 dark:hover:border-indigo-400 transition-all duration-200 shadow-sm"
                    @click="handleSelectedAccessoryExtra(accExtra)"
                  >
                    <div class="flex justify-between items-center">
                      <h3 as="h3" class="text-md font-semibold leading-6 text-gray-900 dark:text-text-dark">
                        {{ accExtra.extra_name }}
                      </h3>
                      <div v-if="accExtra.price > 0" class="ml-3 text-indigo-600 dark:text-indigo-400 font-medium">
                        <span> £{{ accExtra.price }} </span>
                      </div>
                    </div>
                    <div v-if="accExtra.info" class="mt-1">
                      <p class="text-sm text-gray-500 dark:text-gray-400">{{ accExtra.info }}</p>
                    </div>
                  </div>
                </div>
                <template v-if="currentAccesoryExtra.length == 0 && !skipOptionalExtras">
                  <div v-if="currentOptionalExtra.length" class="text-lg font-semibold text-indigo-600 dark:text-indigo-400 mb-2">
                    Choose Extra
                  </div>
                  <div
                    v-for="optionExtra in currentOptionalExtra"
                    :key="optionExtra.$index"
                    class="mt-3 mb-3"
                    track-by="$index"
                  >
                    <div v-if="optionExtra != null" class="border-b border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 p-2 transition-colors duration-200">
                      <div class="flex justify-between items-center">
                        <h3 as="h3" class="text-md font-medium leading-6 text-gray-900 dark:text-text-dark">
                          {{ optionExtra.extra_name }}
                        </h3>
                        <div v-if="optionExtra.price" class="ml-2 flex items-center text-gray-900 dark:text-text-dark">
                          <span> £{{ optionExtra.price }} </span>
                          <button
                            type="button"
                            class="rounded-full border border-transparent shadow-sm focus:outline-none focus:ring-2 ml-3"
                            @click.stop="handleAddSelectedOptionalExtra(optionExtra)"
                          >
                            <PlusCircleIcon
                              class="h-7 w-7 text-e-violet dark:text-indigo-400 mx-auto"
                              aria-hidden="true"
                            />
                          </button>
                        </div>
                      </div>
                      <div v-if="optionExtra.info" class="mt-1">
                        <p class="text-sm text-gray-500 dark:text-gray-400">{{ optionExtra.info }}</p>
                      </div>
                      <template
                        v-for="(optionalExtraItem, index) in Object.values(selectedOptionalExtra)"
                        :key="index"
                      >
                        <div v-if="optionExtra.extra_id == optionalExtraItem.extra_id" class="flex justify-between items-center mt-2 text-gray-900 dark:text-text-dark">
                          <span>{{ optionalExtraItem.quantity }}</span>
                          <span
                            >£{{
                              (optionalExtraItem.price * optionalExtraItem.quantity).toFixed(2)
                            }}</span
                          >
                          <button
                            type="button"
                            class="rounded-full border border-transparent text-white shadow-sm focus:outline-none focus:ring-2 ml-3"
                            @click="handleRemoveSelectedOptionExtra(optionExtra)"
                          >
                            <MinusCircleIcon
                              class="h-7 w-7 text-red-600 dark:text-red-500 mx-auto"
                              aria-hidden="true"
                            />
                          </button>
                        </div>
                      </template>
                    </div>
                  </div>
                </template>
              </div>
              <div
                class="fixed bottom-0 w-full z-50 left-0 right-0 mx-auto px-5 py-2 bg-white dark:bg-gray-800 rounded-lg"
              >
                <div class="grid grid-cols-1 gap-4 grid-cols-2">
                  <div class="flex justify-between items-center mr-8">
                    <button
                      type="button"
                      :disabled="currentAccesoryExtra.length != 0"
                      class="rounded-full border border-transparent text-e-violet dark:text-indigo-400 shadow-sm focus:outline-none focus:ring-2 disabled:text-gray-400"
                      @click="handleItemQuantity()"
                    >
                      <MinusCircleIcon class="h-6 w-6 mx-auto" aria-hidden="true" />
                    </button>
                    <span class="text-lg text-gray-900 dark:text-text-dark">{{ itemQuantity }}</span>
                    <button
                      type="button"
                      :disabled="currentAccesoryExtra.length != 0"
                      class="rounded-full border border-transparent text-e-violet dark:text-indigo-400 shadow-sm focus:outline-none focus:ring-2 disabled:text-gray-400"
                      @click="itemQuantity += 1"
                    >
                      <PlusCircleIcon class="h-6 w-6 mx-auto" aria-hidden="true" />
                    </button>
                  </div>
                  <div class="">
                    <button
                      type="button"
                      :disabled="currentAccesoryExtra.length != 0"
                      class="rounded border w-full border-gray-300 dark:border-gray-600 bg-violet-700 text-white px-2.5 py-1.5 text-sm font-medium shadow-sm focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2 disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:text-gray-400 dark:disabled:text-gray-500"
                      @click="handleAddComplexItem(complexItemData)"
                    >
                      ADD
                    </button>
                  </div>
                </div>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>
